import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  FiPhone, 
  FiMail, 
  FiMapPin, 
  FiInstagram, 
  FiFacebook,
  FiTwitter
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

const Footer = () => {
  const footerRef = useRef(null);

  const { scrollYProgress: footerProgress } = useScroll({
    target: footerRef,
    offset: ["start end", "end end"],
  });

  // Subtle parallax for footer columns
  const col1Y = useTransform(footerProgress, [0, 1], [20, 0]);
  const col2Y = useTransform(footerProgress, [0, 1], [30, 0]);
  const col3Y = useTransform(footerProgress, [0, 1], [40, 0]);
  const col4Y = useTransform(footerProgress, [0, 1], [50, 0]);
  const smoothCol1Y = useSpring(col1Y, { stiffness: 80, damping: 25 });
  const smoothCol2Y = useSpring(col2Y, { stiffness: 80, damping: 25 });
  const smoothCol3Y = useSpring(col3Y, { stiffness: 80, damping: 25 });
  const smoothCol4Y = useSpring(col4Y, { stiffness: 80, damping: 25 });

  const quickLinks = [
    { name: 'Início', path: '/' },
    { name: 'Serviços', path: '/services' },
    { name: 'Fale Conosco', path: '/contact' },
  ];

  const services = [
    { name: 'Automação IA WhatsApp', slug: 'automacao-whatsapp' },
    { name: 'Criador de Rotas', slug: 'rotas-inteligentes' },
    { name: 'DeliveX', slug: 'delivex' }
  ];

  const socialLinks = [
    { icon: <FiInstagram />, url: 'https://www.instagram.com/aytechcwb/', name: 'Instagram' },
    { icon: <FiFacebook />, url: 'https://www.facebook.com/people/AyTech/61576927466865/', name: 'Facebook' },
    { icon: <FiTwitter />, url: 'https://x.com/Aytechcwb', name: 'Twitter' }
  ];

  const colSmoothYs = [smoothCol1Y, smoothCol2Y, smoothCol3Y, smoothCol4Y];

  return (
    <footer ref={footerRef} className="bg-gray-950 text-white relative overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>

      {/* Thin top separator */}
      <div className="h-px bg-white/10" />

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 py-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            style={{ y: colSmoothYs[0] }}
            className="lg:col-span-1"
          >
            <div className="flex items-center space-x-2 mb-6">
              <div>
                <h1 className="text-2xl font-display font-bold tracking-tight text-white">
                  AyTech<span className="font-light italic">.</span>
                </h1>
                <p className="text-xs text-gray-500 -mt-1 uppercase tracking-widest">Soluções Inovadoras</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed text-sm">
              Transformamos negócios através da tecnologia, oferecendo soluções inovadoras 
              em automação, inteligência artificial e gestão de dados.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-9 h-9 bg-white/5 hover:bg-white/15 border border-white/10 rounded-lg flex items-center justify-center transition-all duration-300 text-gray-400 hover:text-white"
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            style={{ y: colSmoothYs[1] }}
          >
            <h4 className="text-sm font-semibold mb-6 uppercase tracking-widest text-gray-300">Links Rápidos</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.path}
                    className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group text-sm"
                  >
                    <span className="w-1 h-1 bg-white rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            style={{ y: colSmoothYs[2] }}
          >
            <h4 className="text-sm font-semibold mb-6 uppercase tracking-widest text-gray-300">Nossos Serviços</h4>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  {service.slug ? (
                    <Link
                      to={`/services/${service.slug}`}
                      className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group text-sm"
                    >
                      <span className="w-1 h-1 bg-white rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      {service.name}
                    </Link>
                  ) : (
                    <span className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group cursor-pointer text-sm">
                      <span className="w-1 h-1 bg-white rounded-full mr-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                      {service.name}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ y: colSmoothYs[3] }}
          >
            <h4 className="text-sm font-semibold mb-6 uppercase tracking-widest text-gray-300">Contato</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <FiMapPin className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                <p className="text-gray-400 leading-relaxed text-sm">
                  Curitiba, Paraná
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <FiPhone className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-gray-400 text-sm">(41) 9 8771-8296</span>
              </div>
              <div className="flex items-center space-x-3">
                <FiMail className="w-4 h-4 text-gray-500 flex-shrink-0" />
                <span className="text-gray-400 text-sm">contato@aytech.online</span>
              </div>
              <div className="pt-2">
                <p className="text-xs text-gray-500 leading-relaxed">
                  <span className="text-gray-400 font-medium">Horário de Atendimento:</span><br />
                  Segunda a Sexta: 9h às 18h<br />
                  Finais de semana: 12h às 18h
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex justify-center items-center">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="text-xs text-gray-600 text-center uppercase tracking-widest"
            >
              © 2025 AyTech — Todos os direitos reservados.
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
